import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { skillColor } from "@/lib/skills";
import { ArabicText, LetterForms } from "@/components/ArabicText";
import { Check, Lock, Clock, ChevronRight } from "lucide-react";
import { t } from "@/i18n";

export default function Learn() {
  const { themeId } = useParams();
  const [theme, setTheme] = useState(null);
  const [done, setDone] = useState(new Set());
  const [alpha, setAlpha] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (themeId === "alphabet") {
      api
        .get("/content/alphabet")
        .then((r) => setAlpha(r.data))
        .catch(() => {});
    }
    api.get("/content/themes").then((r) => {
      const t = r.data.find((x) => x.id === themeId);
      setTheme(t);
    });
    api.get("/progress/summary").then(() => {});
    api
      .get("/content/roadmap")
      .then((r) => {
        const ids = [];
        r.data.forEach((sec) =>
          (sec.nodes || []).forEach((n) => {
            if (n.status === "completed") ids.push(n.id);
          }),
        );
        setDone(new Set(ids));
      })
      .catch(() => {});
  }, [themeId]);

  if (!theme && themeId !== "alphabet")
    return (
      <div className="text-muted-foreground text-center mt-10">{t("lessoneditor.chargement")}</div>
    );

  const color = skillColor(theme?.skill || "alphabet");

  return (
    <div className="max-w-5xl mx-auto px-4 pb-12">
      <div className="mb-10">
        <h1 className="font-head text-3xl sm:text-4xl font-bold flex flex-wrap items-center gap-4">
          {theme?.title || "Écriture & Alphabet"}
          {theme && (
            <ArabicText size="text-3xl" className="text-muted-foreground/50">
              {theme.arabic}
            </ArabicText>
          )}
        </h1>
        <p className="text-muted-foreground text-lg mt-3">
          {theme?.intro ||
            "Le manuel détaillé : chaque lettre, ses 4 formes selon la position, et des exemples de mots."}
        </p>
      </div>

      {/* VUE ALPHABET : Grille sur 2 colonnes pour les lettres */}
      {themeId === "alphabet" && alpha && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {alpha.letters.map((l) => (
            <details
              key={l.char}
              className="group rounded-3xl border-2 bg-card p-5 transition-all hover:border-amber-500/50"
              data-testid={`letter-${l.name}`}
            >
              <summary className="flex items-center gap-5 cursor-pointer list-none outline-none">
                <div
                  className="arabic text-5xl w-16 text-center shrink-0"
                  style={{ color: skillColor("alphabet") }}
                >
                  {l.char}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-xl">{l.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {t("learn.translit")} {l.translit} »
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0 group-open:rotate-90 transition-transform">
                  <ChevronRight size={18} className="text-muted-foreground" />
                </div>
              </summary>
              <div className="mt-6 pt-6 border-t-2">
                <LetterForms char={l.char} connects={l.connects} />
              </div>
            </details>
          ))}
        </div>
      )}

      {/* VUE THÈME : Grille de blocs d'apprentissage type Accueil */}
      {theme && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {theme.lessons.map((l, i) => {
            const isDone = done.has(l.id);
            const locked =
              i > 0 && !done.has(theme.lessons[i - 1].id) && !isDone;

            return (
              <button
                key={l.id}
                data-testid={`lesson-item-${i}`}
                disabled={locked}
                onClick={() => nav(`/learn/lesson/${l.id}`)}
                className={`flex flex-col items-center text-center gap-3 rounded-[2rem] border-2 p-6 transition aspect-square justify-center ${
                  locked
                    ? "opacity-60 bg-muted/20 border-muted cursor-not-allowed"
                    : "hover:-translate-y-1 hover:shadow-md hover:border-primary bg-card"
                }`}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-sm shrink-0 mb-2"
                  style={{
                    background: isDone
                      ? "#16A34A"
                      : locked
                        ? "hsl(var(--muted-foreground)/0.3)"
                        : color,
                  }}
                >
                  {isDone ? (
                    <Check size={32} />
                  ) : locked ? (
                    <Lock size={28} />
                  ) : (
                    i + 1
                  )}
                </div>

                <div
                  className={`font-bold text-sm sm:text-base leading-tight ${locked ? "text-muted-foreground" : "text-foreground"}`}
                >
                  {l.title}
                </div>

                {!locked && (
                  <div className="text-xs sm:text-sm text-muted-foreground">
                    <span className="arabic text-base" dir="rtl">
                      {l.term}
                    </span>{" "}
                    ({l.translit})
                  </div>
                )}

                <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mt-auto bg-secondary/50 px-3 py-1 rounded-full">
                  <Clock size={12} /> {l.minutes} min
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
